/**
 * Memberships → Offers (PLAN-OFFER-01 slice D, mock D · 2026-10-29): every plan waiting
 * on a yes, company-wide — the same rows the 360 Proposals card shows.
 *
 * Four tiles answer the office's questions before the table does: what's out there
 * (Offered), the money a payment away from active (Accepted · Unpaid — amber, the
 * one that must never hide), what lapses this week, and the honest close rate
 * (Took It: offers made in the last 90 days vs plans that went active).
 *
 * Actions are the proposal's own: Open · Resend (same price, new 60 days — opens
 * the proposal page so the staff bar can send it) · Record Payment (the invoice —
 * payment is the activation, there is no Activate button) · Decline (with a reason).
 */
import { planCoversSummary } from "@/lib/planCovers";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Receipt, Send, ShieldCheck, XCircle } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import {
  usePlanOffers, useResendPlanOffer, useDeclinePlanOffer, offerState, OFFER_STATE, offeredOnLabel,
  offerDaysLeft, offerMoney, fmtDay, termLabel, offerProposalUrl, DECLINE_REASONS, daysSince,
  type PlanOfferRow, type OfferState,
} from "./planOffers";

type Pill = "open" | "unpaid" | "expiring" | "closed";

const PILL_LABEL: Record<Pill, string> = { open: "Open", unpaid: "Accepted · Unpaid", expiring: "Expiring", closed: "Declined · Expired" };

function pillOf(o: PlanOfferRow): Pill | null {
  const st = offerState(o);
  if (st === "accepted_unpaid") return "unpaid";
  if (st === "declined" || st === "expired") return "closed";
  if (st === "offered" || st === "viewed" || st === "quoted") {
    const d = offerDaysLeft(o);
    return d !== null && d <= 7 ? "expiring" : "open";
  }
  return null; // active / other leave the list — the Plan tile and the Membership tab own them
}

function Tile({ label, value, sub, tone = "neutral" }: { label: string; value: string; sub?: string | null; tone?: "neutral" | "amber" | "rose" | "emerald" }) {
  return (
    <div className={cn(
      "rounded-2xl border px-4 py-3.5",
      tone === "amber" ? "border-amber-200 bg-amber-50/60" : tone === "rose" ? "border-rose-200 bg-rose-50/60" : tone === "emerald" ? "border-emerald-200 bg-emerald-50/60" : "border-hairline bg-white",
    )}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={cn("mt-1.5 text-[26px] font-bold leading-none tracking-tight tabular-nums [font-family:var(--font-display)]",
        tone === "amber" ? "text-amber-700" : tone === "rose" ? "text-rose-700" : tone === "emerald" ? "text-emerald-700" : "text-slate-900")}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function PlanOffersView() {
  const navigate = useNavigate();
  const { data, isLoading } = usePlanOffers();
  const resend = useResendPlanOffer();
  const decline = useDeclinePlanOffer();
  const [pill, setPill] = useState<Pill>("open");
  const [declining, setDeclining] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const rows = useMemo(() => data ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<Pill, number> = { open: 0, unpaid: 0, expiring: 0, closed: 0 };
    for (const o of rows) { const p = pillOf(o); if (p) c[p] += 1; }
    return c;
  }, [rows]);

  const tiles = useMemo(() => {
    const live = rows.filter((o) => ["offered", "viewed", "quoted"].includes(offerState(o)));
    const unpaid = rows.filter((o) => offerState(o) === "accepted_unpaid");
    const expiring = live.filter((o) => { const d = offerDaysLeft(o); return d !== null && d <= 7; });
    const since = Date.now() - 90 * 864e5;
    const recent = rows.filter((o) => o.offered_at && new Date(o.offered_at).getTime() >= since && offerState(o) !== "other");
    const took = recent.filter((o) => offerState(o) === "active");
    return {
      offered: live.length,
      offeredAmt: live.reduce((s, o) => s + (Number(o.total) || 0), 0),
      viewed: live.filter((o) => o.quote_viewed_at || o.quote_view_count > 0).length,
      unpaid: unpaid.length,
      unpaidAmt: unpaid.reduce((s, o) => s + (Number(o.total) || 0), 0),
      expiring: expiring.length,
      tookPct: recent.length ? Math.round((took.length / recent.length) * 100) : null,
      took: took.length, recent: recent.length,
      tookAmt: took.reduce((s, o) => s + (Number(o.total) || 0), 0),
    };
  }, [rows]);

  const list = rows.filter((o) => pillOf(o) === pill);

  const openCustomer = (o: PlanOfferRow) => navigate(`/customers/${o.custno}?tab=membership&from=agreements`);
  const openOffer = (o: PlanOfferRow) => {
    if (o.offer_quote_id) navigate(`/quotes/${o.offer_quote_id}`);
    else openCustomer(o);
  };
  const doResend = async (o: PlanOfferRow) => {
    setBusy(o.sale_id);
    try {
      const r = await resend.mutateAsync(o.sale_id);
      const url = r.token ? offerProposalUrl({ quote_token: r.token }) : null;
      if (url) window.open(url, "_blank");
    } catch { /* the mutation's error state shows below */ } finally { setBusy(null); }
  };
  const doDecline = async (o: PlanOfferRow, reason: string) => {
    setBusy(o.sale_id);
    try { await decline.mutateAsync({ saleId: o.sale_id, reason }); setDeclining(null); }
    catch { /* shown below */ } finally { setBusy(null); }
  };

  return (
    <div>
      {/* Pills */}
      <div className="mb-4 inline-flex h-9 items-center rounded-full border border-hairline bg-slate-50 p-0.5">
        {(Object.keys(PILL_LABEL) as Pill[]).map((p) => (
          <button key={p} type="button" onClick={() => setPill(p)}
            className={cn("flex h-full items-center gap-1.5 rounded-full px-3.5 text-xs font-medium transition-colors",
              pill === p ? "bg-white text-slate-900 shadow-e1" : "text-slate-500 hover:text-slate-900")}>
            {PILL_LABEL[p]}
            <span className={cn("rounded-full px-1.5 text-[10.5px] font-bold tabular-nums",
              p === "unpaid" && counts[p] > 0 ? "bg-amber-100 text-amber-800" : p === "expiring" && counts[p] > 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600")}>
              {counts[p]}
            </span>
          </button>
        ))}
      </div>

      {/* Tiles */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Offered" value={`${tiles.offered} · ${offerMoney(tiles.offeredAmt)}`}
          sub={tiles.offered ? `${tiles.viewed} viewed · ${tiles.offered - tiles.viewed} never opened` : "nothing out right now"} />
        <Tile label="Accepted · Unpaid" value={`${tiles.unpaid} · ${offerMoney(tiles.unpaidAmt)}`} tone={tiles.unpaid ? "amber" : "neutral"}
          sub="activate when their invoices are paid" />
        <Tile label="Expiring This Week" value={String(tiles.expiring)} tone={tiles.expiring ? "rose" : "neutral"}
          sub="60 days · Resend keeps the price" />
        <Tile label="Took It · Last 90 Days" value={tiles.tookPct === null ? "—" : `${tiles.tookPct}%`} tone={tiles.tookPct !== null && tiles.tookPct >= 30 ? "emerald" : "neutral"}
          sub={tiles.recent ? `${tiles.took} of ${tiles.recent} offers · ${offerMoney(tiles.tookAmt)} of plans` : "no offers made yet"} />
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="m-4 h-24 animate-pulse rounded-xl bg-slate-50" />
        ) : list.length === 0 ? (
          <div className="p-10 text-center">
            <ShieldCheck className="mx-auto h-6 w-6 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-700">
              {pill === "open" ? "No plan offers waiting" : pill === "unpaid" ? "Nothing accepted and unpaid" : pill === "expiring" ? "Nothing expiring this week" : "Nothing declined or expired"}
            </p>
            <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-slate-500">
              A plan offered on a proposal, ticked by a homeowner, or priced in the Plan Builder shows here — the same rows the customer&apos;s 360 shows.
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Plan</th>
                <th className="px-3 py-2.5">Offered On</th>
                <th className="px-3 py-2.5">Sent</th>
                <th className="px-3 py-2.5">Last Seen</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 text-right"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((o) => {
                const st = offerState(o);
                const S = OFFER_STATE[st];
                const left = offerDaysLeft(o);
                const isBusy = busy === o.sale_id;
                return (
                  <tr key={o.sale_id} className="align-top transition-colors hover:bg-slate-50/70">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => openCustomer(o)} className="block text-left">
                        <span className="block font-semibold text-slate-800">{o.customer_name?.replace(/\s*\d{6,7}\s*$/, "") || o.custno}</span>
                        <span className="block text-xs text-slate-500">{[o.address, o.city].filter(Boolean).join(" · ") || o.custno}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className="block font-medium text-slate-800">{o.plan_name.replace(/^Sedgwick Heating\s+/i, "")} · {termLabel(o.term)}{(o.systems ?? 1) > 1 ? ` · ${o.systems} systems` : ""}</span>
                      {o.base_name && <span className="block text-xs text-slate-500">{o.base_name}{planCoversSummary(o.covers) ? ` · ${planCoversSummary(o.covers)}` : ""}</span>}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <span className="block">{offeredOnLabel(o)}</span>
                      {o.quote_title && o.offered_on !== "repair" && <span className="block max-w-[22ch] truncate text-xs text-slate-500">{o.quote_title}</span>}
                    </td>
                    <td className="px-3 py-3 tabular-nums text-slate-700">{fmtDay(o.offered_at ?? o.created_at)}</td>
                    <td className="px-3 py-3 tabular-nums text-slate-700">
                      {o.quote_last_viewed_at || o.quote_viewed_at
                        ? <span className="inline-flex items-center gap-1">{fmtDay(o.quote_last_viewed_at ?? o.quote_viewed_at)}{o.quote_view_count > 1 && <span className="text-xs text-slate-400">· {o.quote_view_count}×</span>}</span>
                        : <span className="text-slate-400">{o.offered_on === "office" ? "—" : "never opened"}</span>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold", S.chip)}>
                        {S.label}
                        {st === "offered" || st === "viewed" ? (left !== null ? ` · ${left <= 0 ? "today" : `${left} d`}` : "") : ""}
                      </span>
                      {st === "accepted_unpaid" && (
                        <span className="mt-1 block text-[11px] text-amber-800/80">
                          {o.invoice_no || o.esc_invoice_no
                            ? `invoice ${o.invoice_no ?? o.esc_invoice_no}${o.invoice_balance != null ? ` · ${offerMoney(o.invoice_balance)} open` : ""}`
                            : `no invoice yet · accepted ${fmtDay(o.accepted_at)}`}
                          {(daysSince(o.accepted_at) ?? 0) >= 30 ? " · 30+ days" : ""}
                        </span>
                      )}
                      {st === "declined" && o.declined_reason && <span className="mt-1 block max-w-[24ch] truncate text-[11px] text-slate-500">{o.declined_reason}</span>}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold tabular-nums text-slate-800">{offerMoney(o.total)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center justify-end gap-1.5">
                        {declining === o.sale_id ? (
                          <span className="flex flex-wrap justify-end gap-1">
                            {DECLINE_REASONS.map((r) => (
                              <button key={r} type="button" disabled={isBusy} onClick={() => void doDecline(o, r)}
                                className="rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">{r}</button>
                            ))}
                            <button type="button" onClick={() => setDeclining(null)} className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
                          </span>
                        ) : (
                          <>
                            {st === "accepted_unpaid" && (
                              o.offer_invoice_id
                                ? <button type="button" onClick={() => navigate(`/invoices/${o.offer_invoice_id}`)} title="Open the invoice — recording its payment activates the plan"
                                    className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-[11.5px] font-bold text-white hover:bg-amber-700"><Receipt className="h-3 w-3" /> Record Payment</button>
                                : <button type="button" onClick={() => openOffer(o)} title="The invoice hasn't been built yet — open the quote"
                                    className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2.5 py-1 text-[11.5px] font-bold text-white hover:bg-slate-700">Open</button>
                            )}
                            {(st === "offered" || st === "viewed" || st === "expired" || st === "declined") && o.quote_token && (
                              <button type="button" disabled={isBusy} onClick={() => void doResend(o)} title="Same price, a new 60 days — opens the proposal page to send it"
                                className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[11.5px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Resend
                              </button>
                            )}
                            {st !== "accepted_unpaid" && (
                              <button type="button" onClick={() => openOffer(o)} title={o.offer_quote_id ? "Open the proposal" : "Open the customer"}
                                className="inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[11.5px] font-bold text-slate-700 hover:bg-slate-50">Open <ArrowRight className="h-3 w-3" /></button>
                            )}
                            {(st === "offered" || st === "viewed" || st === "quoted" || st === "accepted_unpaid" || st === "expired") && (
                              <button type="button" onClick={() => setDeclining(o.sale_id)} title="Decline this offer, with a reason"
                                className="rounded-md p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600"><XCircle className="h-4 w-4" /></button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {(resend.isError || decline.isError) && (
          <div className="border-t border-rose-100 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-700">That didn&apos;t save — try again.</div>
        )}
      </Card>
    </div>
  );
}

/** Count for the Memberships tab badge: open + accepted-unpaid offers. */
export function useOpenOfferCount(): number | null {
  const { data } = usePlanOffers();
  if (!data) return null;
  return data.filter((o) => { const p = pillOf(o); return p === "open" || p === "unpaid" || p === "expiring"; }).length;
}

export type { OfferState };
